<template>
  <div v-if="isValidAgeCategory" class="container">
    <!-- ヘッダー -->
    <div class="notice__header">
      <img class="notice__header__img" src="/img/reality_icon.png" />
      <div class="notice__header__right">
        <div class="notice__header__contributor-name">{{ $t('age_verification__header__team_name') }}</div>
        <div class="notice__header__title">
          {{ $t('age_verification__header__title') }}
        </div>
      </div>
    </div>

    <!-- 本文 -->
    <div class="notice__body">
      <template v-if="verificationStatus === VerificationStatus.EXPIRED">
        <p>{{ $t('age_verification__thank_you_intro') }}</p>
        <p>{{ $t('age_verification__expired__suspended') }}</p>
        <p>{{ $t('age_verification__expired__resume_guide') }}</p>
        <i18n path="age_verification__note__region" tag="p" class="notice__body__note">
          <template #termsLink>
            <a href="https://reality.app/legal/terms_of_use.html">{{ $t('age_verification__terms_of_use') }}</a>
          </template>
        </i18n>
        <p class="notice__body__note">{{ $t('age_verification__note__age_result') }}</p>
      </template>
      <template v-else-if="verificationStatus === VerificationStatus.VERIFIED">
        <p>{{ $t('age_verification__thank_you_intro') }}</p>
        <p>{{ $t('age_verification__complete__success_title') }}</p>
      </template>
      <template v-else>
        <p>{{ $t('age_verification__individual_notice') }}</p>
        <p>{{ $t('age_verification__thank_you_intro') }}</p>
        <p>{{ $t('age_verification__reported__body') }}</p>
        <p>{{ $t('age_verification__deadline_guide', { deadline: formattedDeadline }) }}</p>
        <p class="notice__body__bold">{{ $t('age_verification__deadline_warning') }}</p>
        <i18n path="age_verification__note__region" tag="p" class="notice__body__note">
          <template #termsLink>
            <a href="https://reality.app/legal/terms_of_use.html">{{ $t('age_verification__terms_of_use') }}</a>
          </template>
        </i18n>
        <p class="notice__body__note">{{ $t('age_verification__note__age_result') }}</p>
      </template>
    </div>

    <!-- 年齢認証開始ボタン -->
    <div class="verification-button-wrapper">
      <button
        class="verification-button"
        :disabled="
          isLoading ||
          isStarting ||
          !token ||
          verificationStatus === VerificationStatus.VERIFIED
        "
        @click="startVerification"
      >
        {{ buttonText }}
      </button>
    </div>

    <!-- ボタン下テキスト -->
    <div class="notice__after-button">
      <i18n path="age_verification__help_guide" tag="p">
        <template #helpLink>
          <a href="https://help.reality.app/hc/ja">{{ $t('age_verification__help') }}</a>
        </template>
      </i18n>
      <p>
        {{ $t('age_verification__closing_message_1') }}<br />
        {{ $t('age_verification__closing_message_2') }}
      </p>
    </div>

    <!-- Kidentifyプライバシー情報 -->
    <div class="notice__privacy">
      <p>{{ $t('age_verification__privacy__data_handling') }}</p>
      <p>{{ $t('age_verification__privacy__data_location') }}</p>
      <i18n path="age_verification__privacy__policy_links" tag="p">
        <template #kidentifyLink>
          <a href="https://www.k-id.com/privacy-policy">{{ $t('age_verification__privacy__kidentify_policy') }}</a>
        </template>
        <template #iosLink>
          <a href="https://reality.app/legal/privacy_policy_ios.html">iOS</a>
        </template>
        <template #androidLink>
          <a href="https://reality.app/legal/privacy_policy_android.html">Android</a>
        </template>
      </i18n>
    </div>

    <!-- フッター -->
    <div class="footer">
      <div class="social-icons">
        <a
          href="https://x.com/REALITY_app"
          class="social-link"
          aria-label="X"
        >
          <img
            src="/img/campaign/reality_con_2025/x_logo.png"
            alt="X"
            class="social-icon-img"
          />
        </a>
        <a
          href="https://discord.com/invite/wBgtpCq"
          class="social-link"
          aria-label="Discord"
        >
          <img
            src="/img/campaign/reality_con_2025/discord_logo.png"
            alt="Discord"
            class="social-icon-img"
          />
        </a>
        <a
          href="https://www.youtube.com/@REALITY_app"
          class="social-link"
          aria-label="YouTube"
        >
          <img
            src="/img/campaign/reality_con_2025/youtube_logo.png"
            alt="YouTube"
            class="social-icon-img"
          />
        </a>
      </div>
      <div class="copyright">©REALITY, inc.</div>
    </div>

    <!-- エラーアラート -->
    <alert
      v-if="shouldShowAlert"
      :alertContent="alertContent"
      @alertButtonClick="closeAlertModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import Alert from '@/components/Alert.vue'
import { apiProxy } from '@/api/appGatewayServerV1ApiProxy'
import { ApiError } from '@/api/error'
import {
  AgeCategory,
  AgeCategoryValue,
  VerificationStatus,
  VerificationStatusValue,
} from '@/api/schema/ageVerification'
import { getToken } from '@/utils/token'
import { showGenericNetworkError } from '@/utils/handle_error'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'

@Component({
  components: { Alert },
})
export default class NoticeAgeVerification extends Vue {
  @Prop({ type: String, required: true })
  ageCategory!: string

  VerificationStatus = VerificationStatus

  isLoading = true
  isStarting = false
  deadline: string | null = null
  verificationStatus: VerificationStatusValue | null = null

  get isValidAgeCategory(): boolean {
    return this.ageCategory === 'digital_youth' || this.ageCategory === 'adult'
  }

  async mounted() {
    if (!this.isValidAgeCategory) {
      this.$router.replace({ name: 'notfound' })
      return
    }

    // iOS/Android以外はダウンロードページへリダイレクト
    const nativeInterface = defaultNativeInterfaceClient()
    if (nativeInterface.client === Client.native) {
      this.$router.replace({ name: 'download' })
      return
    }

    await this.fetchVerificationStatus()
  }

  async fetchVerificationStatus() {
    if (!this.token) {
      this.isLoading = false
      return
    }

    try {
      const response = await apiProxy().age_verification.status({
        ageCategory: this.ageCategoryValue,
      })

      this.verificationStatus = response.verificationStatus
      if (response.deadline) {
        this.deadline = response.deadline
      }
    } catch (e) {
      this.handleApiError(e)
    } finally {
      this.isLoading = false
    }
  }

  shouldShowAlert = false
  alertContent = {
    content: { header: '', body: '' },
    alertItems: [] as { type: string; title: string }[],
  }

  get token(): string | undefined {
    return getToken()
  }

  get buttonText(): string {
    if (this.verificationStatus === VerificationStatus.VERIFIED) {
      return this.$t('age_verification__button__verified') as string
    }
    return this.$t('age_verification__button__start') as string
  }

  get ageCategoryValue(): AgeCategoryValue {
    return this.ageCategory === 'digital_youth'
      ? AgeCategory.DIGITAL_YOUTH
      : AgeCategory.ADULT
  }

  get formattedDeadline(): string {
    if (!this.deadline) {
      return this.$t('age_verification__default_deadline') as string
    }
    const date = new Date(this.deadline)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}/${month}/${day} ${hours}:${minutes}`
  }

  async startVerification() {
    if (this.isStarting) {
      return
    }

    this.isStarting = true

    try {
      const response = await apiProxy().age_verification.start({
        ageCategory: this.ageCategoryValue,
      })

      if (!response.verificationUrl) {
        this.openAlertModal(this.$t('errorOccurred') as string)
        return
      }

      const nativeInterface = defaultNativeInterfaceClient()
      try {
        nativeInterface.openAgeVerification(response.verificationUrl)
      } catch (e) {
        window.location.href = response.verificationUrl
      }
    } catch (e) {
      this.handleApiError(e)
    } finally {
      this.isStarting = false
    }
  }

  handleApiError(e: unknown) {
    if (e instanceof ApiError) {
      if (e.status === 429) {
        this.openAlertModal(this.$t('age_verification__button__too_many_requests') as string)
      } else {
        this.openAlertModal(this.$t('errorOccurred') as string)
      }
    } else {
      showGenericNetworkError()
    }
  }

  openAlertModal(message: string) {
    this.alertContent = {
      content: {
        header: this.$t('noticeError') as string,
        body: message,
      },
      alertItems: [{ type: 'preffered', title: 'OK' }],
    }
    this.shouldShowAlert = true
  }

  closeAlertModal() {
    this.shouldShowAlert = false
  }
}
</script>

<style scoped>
.container {
  padding: 12px 15px;
  max-width: 600px;
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.6;
}

/* ヘッダー */
.notice__header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.notice__header__img {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  object-fit: contain;
}

.notice__header__right {
  flex: 1;
}

.notice__header__contributor-name {
  font-size: 12px;
  color: #888;
}

.notice__header__title {
  font-size: 14px;
  font-weight: bold;
  margin-top: 2px;
}

/* 本文 */
.notice__body {
  margin-bottom: 0;
}

.notice__body p {
  margin: 0 0 16px 0;
}

.notice__body__bold {
  font-weight: bold;
}

.notice__body__note {
  color: #7b7b7b;
  font-size: 12px;
  margin-bottom: 4px;
}

.notice__body__note:last-child {
  margin-bottom: 0;
}

/* ボタン */
.verification-button-wrapper {
  text-align: center;
  margin: 32px 0 24px;
}

.verification-button {
  background-color: #188DFF;
  color: white;
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}

.verification-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.verification-button:hover:not(:disabled) {
  background-color: #0f7ae6;
}

/* ボタン下テキスト */
.notice__after-button {
  margin-top: 0;
}

.notice__after-button p {
  margin: 0 0 16px 0;
}

/* Kidentifyプライバシーセクション */
.notice__privacy {
  margin-top: 16px;
  color: #7b7b7b;
  font-size: 11px;
  line-height: 1.5;
  letter-spacing: 0.44px;
}

.notice__privacy p {
  margin: 0 0 4px 0;
}

/* リンク */
.notice__body__note a,
.notice__after-button a,
.notice__privacy a {
  color: inherit;
  text-decoration: underline;
}

/* フッター */
.footer {
  margin-top: 40px;
  padding-bottom: 16px;
}

.social-icons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 16px;
}

.social-link {
  text-decoration: none;
}

.social-icon-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.copyright {
  text-align: center;
  color: #808080;
  font-size: 14px;
}
</style>
